/**
 * Reloads the page when a chunk has stopped existing on the server.
 *
 * ## Why it's needed
 *
 * Routes are loaded on demand, and each build gives them a hashed name:
 * `index-B79y6gPw.js`. When deploying, the names change and the previous
 * ones disappear.
 *
 * That leaves an unavoidable window: whoever has the app open during a
 * deploy keeps in memory an HTML that points to files that have just
 * stopped existing. When navigating to another screen, the request returns
 * 404 and the app shows `Failed to fetch dynamically imported module`.
 *
 * **No cache header avoids that case.** Headers prevent the browser from
 * reusing an old HTML on a new visit; they can do nothing for a tab that is
 * already open. That's why this lives in the code.
 *
 * Reloading solves the problem because it brings in the new HTML, which
 * requests the files that do exist. The person sees a flicker instead of an
 * error screen.
 *
 * ## Why only once
 *
 * If the failure were not due to a deploy —the network being down, a file
 * that genuinely does not exist— reloading fixes nothing and the loop would
 * leave the screen flickering forever. The flag lives in `sessionStorage`
 * and is cleared as soon as a load succeeds, so a second deploy during the
 * same session is covered again.
 */

const MARK = 'lnm:stale-chunk-reload';

/** A successful load returns the reload credit. */
const markChunkLoadSucceeded = (): void => {
  try {
    sessionStorage.removeItem(MARK);
  } catch {
    // `sessionStorage` may not be available —private mode in some
    // browsers—. Not being able to remember the attempt does not justify
    // breaking the app.
  }
};

/**
 * Reloads if it's the first time in this session.
 *
 * Returns `true` if the reload is under way, so the caller doesn't render
 * an error in the meantime.
 */
const reloadOnceForStaleChunk = (): boolean => {
  try {
    if (sessionStorage.getItem(MARK)) return false;
    sessionStorage.setItem(MARK, '1');
  } catch {
    // Without storage there's no way to count attempts. Not reloading is
    // preferred over risking an infinite loop.
    return false;
  }

  window.location.reload();
  return true;
};

export { markChunkLoadSucceeded, reloadOnceForStaleChunk };
